<template>
    <app-layout>
        <page-header :title="trans('references.overview.title')" title-in-app-header>
            <div class="mt-3 flex flex-wrap items-start gap-3">
                <filter-bar v-model="activeFilters" :config="filterbarConfig" class="min-w-0 flex-1">
                    <template #results-option="{ option }"
                        ><span :class="getTaskResolutionColor(option.value, 'text')" class="font-medium">{{ option.label }}</span></template
                    >
                </filter-bar>
                <export-menu
                    v-if="hasAnyPermission('reference.export')"
                    :active-query-params="activeFilters"
                    :allowed-exports="['csv', 'xlsx', 'pdf']"
                    :no-item-selected-tooltip="trans('test_records.overview.export.tooltip.no_item_selected')"
                    :selection="selection"
                    class="shrink-0"
                    export-group-key="references-overview-export"
                    routeName="tasks.export"
                />
            </div>
        </page-header>
        <references-table :columns="fields" :items="tasks" :query-params="activeFilters" :selection-enabled="selectionEnabled" />
    </app-layout>
</template>
<script setup>
import PageHeader from '@/Components/PageHeader.vue';
import AppLayout from '@/Layouts/AppLayout.vue';
import { computed, ref } from 'vue';
import { trans } from '@/mixins/i18n.js';
import { usePage } from '@inertiajs/vue3';
import FilterBar from '@/Components/FilterBar.vue';
import useSelectedItems from '@/Utils/UseSelectedItems';
import ExportMenu from '@/Components/ExportMenu.vue';
import { hasAnyPermission } from '@/mixins/hasAnyPermission.js';
import ReferencesTable from '@/Components/ReferencesTable.vue';
import { getTaskResolutionColor } from '@/Pages/TaskList/utils.js';
import useReloadOnFocus from '../../Utils/UseReloadOnFocus';

const props = defineProps({
    tasks: Object,
});

const page = usePage();
const tasksMeta = computed(() => page.props.tasks?.meta || {});
const selection = useSelectedItems('global-references', tasksMeta);

const activeFilters = ref(null);
const filterbarConfig = computed(() => ({
    route: route('references.index'),
    routeOptions: { only: ['tasks'] },
    additionalData: () => ({ selection: selection.getSelectionAsQueryParams() }),
    onLoadSuccess: (data) => {
        const selectionData = data.props.tasks?.meta?.selection;
        if (selectionData !== undefined) {
            selection.setSelection(selectionData);
        }
    },
    defaultSort: { column: 'date', asc: false },
    filters: [
        { key: 'search', type: 'search', placeholder: trans('references.overview.filter.search') },
        {
            key: 'taskTypes',
            type: 'multi-select',
            label: trans('tasks.overview.filter.task_type'),
            options: page.props.filters?.taskTypes?.data ?? [],
        },
        { key: 'date', type: 'date-range', label: trans('activities.overview.cell.date'), startKey: 'start', endKey: 'end' },
        {
            key: 'results',
            type: 'multi-select',
            label: trans('references.overview.filter.result'),
            options: page.props.filters?.results?.data ?? [],
        },
        {
            key: 'users',
            type: 'multi-select',
            label: trans('activities.overview.cell.user'),
            optionLabelSlot: 'truncate',
            options:
                page.props.filters.users?.data.map(({ name, id }) => ({
                    label: name,
                    value: id,
                })) || [],
        },
        {
            type: 'multi-select',
            key: 'specificLocations',
            label: trans('activities.overview.cell.specific_location'),
            options: specificLocations.value,
            showSearchbar: true,
        },
    ],
}));

const fields = [
    { key: 'task', label: trans('references.overview.cell.task'), sorter: false },
    { key: 'result', label: trans('references.overview.cell.result'), sorter: false },
    { key: 'date', label: trans('references.overview.cell.date'), sorter: false },
    { key: 'title', label: trans('references.overview.cell.title'), sorter: false },
    { key: 'asset', label: trans('tasks.overview.item.asset.title'), sorter: false },
    { key: 'location', label: trans('tasks.overview.item.location.title'), sorter: false },
    { key: 'user', label: trans('references.overview.cell.user'), sorter: false },
    { key: 'presence', label: 'Presence', sorter: false, _style: 'width: 40px !important' },
    { key: 'attachments', label: 'Attachments', sorter: false, _style: 'width: 40px !important' },
    { key: 'actions', sorter: false, _style: 'width: 40px !important' },
];

const selectionEnabled = computed(() => (hasAnyPermission('reference.export') ? 'global-references' : false));
const specificLocations = computed(() => (page.props.filters.specificLocations?.data || []).map((loc) => ({ label: loc.name, value: loc.id })));

useReloadOnFocus({ only: ['tasks'] });
</script>
